import { Check, RotateCcw, Trash2, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Broker } from 'shared';
import { toast } from 'sonner';

import { ImportDropzone } from '../components/domain/ImportDropzone';
import { ImportPreviewTable } from '../components/domain/ImportPreviewTable';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Label } from '../components/ui/Label';
import { useAccounts } from '../features/accounts/queries';
import {
  useCommitImport,
  useDiscardImport,
  useResetImport,
  useUploadImport,
} from '../features/imports/mutations';
import { mandateLabel } from '../lib/accountMandate';
import type { ImportDto } from '../lib/api/endpoints';

export function Import() {
  const accounts = useAccounts();
  const uploadImport = useUploadImport();
  const commitImport = useCommitImport();
  const discardImport = useDiscardImport();
  const resetImport = useResetImport();
  const [accountId, setAccountId] = useState('');
  const [importId, setImportId] = useState<string | null>(null);
  const [currentImport, setCurrentImport] = useState<ImportDto | null>(null);
  const imported = currentImport ?? uploadImport.data;

  useEffect(() => {
    if (uploadImport.data) {
      setCurrentImport(uploadImport.data);
      setImportId(uploadImport.data.id);
    }
  }, [uploadImport.data]);

  const selectedAccount = useMemo(
    () => accounts.data?.find((account) => account.id === accountId) ?? null,
    [accounts.data, accountId],
  );
  const broker: Broker | null = selectedAccount?.broker ?? null;

  function onFileAccepted(file: File) {
    if (!accountId) {
      toast.error('Select an account before uploading.');
      return;
    }
    if (!broker) {
      toast.error('Selected account is missing broker details.');
      return;
    }
    uploadImport.mutate(
      { accountId, broker, file },
      {
        onSuccess: (record) => {
          setImportId(record.id);
          setCurrentImport(record);
          toast.success('CSV parsed for preview.');
        },
      },
    );
  }

  function onCommit() {
    if (!importId) return;
    commitImport.mutate(importId, {
      onSuccess: (record) => {
        setCurrentImport(record);
        toast.success(
          'Import committed successfully. View your positions to see the imported trades.',
        );
      },
      onError: (err) => {
        toast.error(`Commit failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      },
    });
  }

  function onNewImport() {
    setImportId(null);
    setCurrentImport(null);
    uploadImport.reset();
  }

  function onRetryCommit() {
    if (!importId) return;
    resetImport.mutate(importId, {
      onSuccess: (record) => {
        setCurrentImport(record);
        toast.success('Import reset. Retry commit when ready.');
      },
      onError: (err) => {
        toast.error(`Reset failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      },
    });
  }

  function onDiscard() {
    if (!importId) return;
    discardImport.mutate(importId, {
      onSuccess: () => {
        setImportId(null);
        setCurrentImport(null);
        uploadImport.reset();
        toast.success('Import discarded.');
      },
    });
  }

  if (accounts.isLoading) {
    return <main className="p-6 text-text-muted">Loading accounts...</main>;
  }

  if (accounts.isError || !accounts.data) {
    return <main className="p-6 text-danger">Could not load accounts.</main>;
  }

  const summary = imported?.summary;
  const rows = imported?.rows ?? [];
  const canRetry = imported?.status === 'FAILED' || imported?.status === 'COMMITTING';
  const canCommit =
    summary !== undefined &&
    summary !== null &&
    summary.errorRows === 0 &&
    imported?.status !== 'COMMITTED' &&
    imported?.status !== 'COMMITTING' &&
    imported?.status !== 'FAILED';

  return (
    <main className="min-h-screen bg-bg px-4 py-6 text-text sm:px-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Import</h1>
            <p className="mt-1 text-sm text-text-muted">Broker CSV staging and reconciliation.</p>
          </div>
          <Button asChild variant="secondary">
            <Link to="/settings">Settings</Link>
          </Button>
        </header>

        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          <div className="space-y-4">
            <Card
              header={
                <div>
                  <h2 className="text-base font-semibold">1. Account</h2>
                  <p className="text-sm text-text-muted">Choose where the executions belong.</p>
                </div>
              }
            >
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="import-account">Account</Label>
                  <select
                    id="import-account"
                    className="h-10 w-full rounded border border-border bg-bg px-3 text-sm text-text"
                    value={accountId}
                    onChange={(event) => setAccountId(event.target.value)}
                  >
                    <option value="">Select account</option>
                    {accounts.data.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name} | {account.broker} | {mandateLabel(account.mandate)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="import-broker">Broker</Label>
                  <div
                    id="import-broker"
                    className="flex h-10 w-full items-center rounded border border-border bg-bg px-3 text-sm text-text-muted"
                  >
                    {broker ?? 'Select an account'}
                  </div>
                </div>
                {selectedAccount ? (
                  <p className="text-sm text-text-muted">
                    {selectedAccount.broker} | {mandateLabel(selectedAccount.mandate)} |{' '}
                    {selectedAccount.currency}
                  </p>
                ) : null}
              </div>
            </Card>

            <Card
              header={
                <div>
                  <h2 className="text-base font-semibold">2. Upload</h2>
                  <p className="text-sm text-text-muted">CSV only, 10MB maximum.</p>
                </div>
              }
            >
              <ImportDropzone
                brokerLabel={broker ?? 'broker'}
                disabled={uploadImport.isPending}
                onFileAccepted={onFileAccepted}
              />
              {uploadImport.isError ? (
                <p className="mt-3 text-sm text-danger">Upload failed.</p>
              ) : null}
              {imported ? (
                <dl className="mt-4 space-y-2 text-sm">
                  <div>
                    <dt className="text-text-muted">File</dt>
                    <dd className="font-medium">{imported.fileName}</dd>
                  </div>
                  <div>
                    <dt className="text-text-muted">SHA-256</dt>
                    <dd className="break-all font-mono text-xs">{imported.fileHash}</dd>
                  </div>
                </dl>
              ) : null}
            </Card>
          </div>

          <Card
            header={
              <div>
                <h2 className="text-base font-semibold">3. Preview</h2>
                <p className="text-sm text-text-muted">Commit only clean staged rows.</p>
              </div>
            }
          >
            {summary ? (
              <div className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded border border-border bg-bg p-3">
                    <p className="text-sm text-text-muted">Rows parsed</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums">{summary.totalRows}</p>
                  </div>
                  <div className="rounded border border-border bg-bg p-3">
                    <p className="text-sm text-text-muted">Duplicates</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums">
                      {summary.duplicateRows}
                    </p>
                  </div>
                  <div className="rounded border border-border bg-bg p-3">
                    <p className="text-sm text-text-muted">Errors</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums">{summary.errorRows}</p>
                  </div>
                </div>

                <ImportPreviewTable rows={rows} />

                {imported.status === 'COMMITTED' ? (
                  <div className="space-y-3 rounded border border-success/20 bg-success/5 p-4">
                    <p className="text-sm font-medium text-success">
                      ✓ Import committed successfully
                    </p>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button type="button" className="gap-2" onClick={onNewImport}>
                        <Upload className="h-4 w-4" aria-hidden="true" />
                        Import another file
                      </Button>
                      <Button asChild variant="secondary">
                        <Link to="/positions">View positions</Link>
                      </Button>
                    </div>
                  </div>
                ) : null}

                {imported.status === 'FAILED' ? (
                  <p className="text-sm text-danger">{imported.error ?? 'Import commit failed.'}</p>
                ) : null}

                {imported.status !== 'COMMITTED' ? (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      type="button"
                      className="gap-2"
                      disabled={!canCommit || commitImport.isPending || resetImport.isPending}
                      onClick={onCommit}
                    >
                      <Check className="h-4 w-4" aria-hidden="true" />
                      Commit
                    </Button>
                    {canRetry ? (
                      <Button
                        type="button"
                        variant="secondary"
                        className="gap-2"
                        disabled={resetImport.isPending || commitImport.isPending}
                        onClick={onRetryCommit}
                      >
                        <RotateCcw className="h-4 w-4" aria-hidden="true" />
                        Retry commit
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="destructive"
                      className="gap-2"
                      disabled={discardImport.isPending}
                      onClick={onDiscard}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      Discard
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex min-h-72 flex-col items-center justify-center rounded border border-border bg-bg text-center">
                <Upload className="mb-3 h-8 w-8 text-text-muted" aria-hidden="true" />
                <p className="text-sm text-text-muted">No import staged.</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </main>
  );
}
